import { cn } from "@/common/helpers/utils";
import { useId } from "react";
import { VEHICLE_GROUND_Y } from "./vehicle-outline";

export interface VehicleLoaderProps {
  className?: string;
  /** Draws with `currentColor` instead of the primary color, e.g. on filled buttons. Supported by the wheel variant. */
  inheritColor?: boolean;
}

interface LoaderSvgProps extends Omit<VehicleLoaderProps, "inheritColor"> {
  viewBox: string;
  modifiers?: string;
  children: React.ReactNode;
}

export function LoaderSvg({ viewBox, modifiers, className, children }: LoaderSvgProps) {
  return (
    <svg
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={cn("vehicle-loader-root", modifiers, className)}
    >
      {children}
    </svg>
  );
}

export function useSvgId(prefix: string) {
  return `${prefix}-${useId().replace(/[^\w-]/g, "")}`;
}

interface FadeMaskProps {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export function HorizontalFadeMask({ id, x, y, width, height }: FadeMaskProps) {
  const gradientId = `${id}-gradient`;

  return (
    <defs>
      <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
        <stop offset="0" stopColor="white" stopOpacity="0" />
        <stop offset="0.2" stopColor="white" />
        <stop offset="0.8" stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0" />
      </linearGradient>
      <mask id={id} maskUnits="userSpaceOnUse" x={x} y={y} width={width} height={height}>
        <rect x={x} y={y} width={width} height={height} fill={`url(#${gradientId})`} />
      </mask>
    </defs>
  );
}

interface RoadProps {
  x1: number;
  x2: number;
  y?: number;
}

export function DashedRoad({ x1, x2, y = VEHICLE_GROUND_Y + 0.5 }: RoadProps) {
  return (
    <line
      x1={x1}
      x2={x2}
      y1={y}
      y2={y}
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeDasharray="6 5"
      className="vehicle-loader-road-dashes stroke-muted-foreground/40"
    />
  );
}

export function GroundShadow() {
  return <ellipse cx={32} cy={VEHICLE_GROUND_Y} rx={24} ry={1.4} className="vehicle-loader-shadow fill-primary/15" />;
}

export const SPARKLE_PATH = "M0-3.5Q.4-.4 3.5 0Q.4.4 0 3.5Q-.4.4-3.5 0Q-.4-.4 0-3.5Z";
